import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// ── Config & Utilities ──
import { KNOWN_CUSTOM_DOMAINS, PAGE_PATH_MAP, PLATFORM_MAIN_HOSTS, isProductionHost as isProductionHostName } from "./config/domains";
import {
  isDevOrPreview,
  detectTenantFromPath, detectTenantSlugFromHost, isCustomDomainHost,
} from "./utils/tenantDetection";
import {
  LazyFallback, LaunchFallback, TenantLoading, TenantError,
} from "./components/RouteGuards";

// ── Lazy-loaded components ──

// Landing-only heavy components (lazy-loaded so they don't bloat admin/worker bundles)
const CookieBanner = lazy(() => import("./components/CookieBanner").then(m => ({ default: m.CookieBanner })));

const AdminPanel = lazy(() => import("./components/AdminPanel").then(m => ({ default: m.AdminPanel })));
const RedirectToLogin = () => { useEffect(() => { window.location.replace("/login"); }, []); return <LaunchFallback />; };
/** Start page of the single installable app: send the user to the right shell for the saved session, else to login. */
const AppEntry = () => {
  useEffect(() => {
    let dest = "/login";
    try {
      const slug = localStorage.getItem("tenantSlug");
      const base = slug ? `/t/${slug}` : "";
      if (localStorage.getItem("adminToken") && localStorage.getItem("adminUser")) dest = `${base}/admin`;
      else if (localStorage.getItem("workerToken") && localStorage.getItem("workerUser")) dest = `${base}/worker`;
    } catch { /* storage unavailable -> login */ }
    window.location.replace(dest);
  }, []);
  return <LaunchFallback />;
};
const UnifiedLogin = lazy(() => import("./components/UnifiedLogin").then(m => ({ default: m.UnifiedLogin })));
const SetPasswordPage = lazy(() => import("./components/SetPasswordPage").then(m => ({ default: m.SetPasswordPage })));
const WorkerPortal = lazy(() => import("./components/WorkerPortal").then(m => ({ default: m.WorkerPortal })));
const SuperAdminLogin = lazy(() => import("./components/SuperAdminLogin").then(m => ({ default: m.SuperAdminLogin })));
const SuperAdminPanel = lazy(() => import("./components/SuperAdminPanel").then(m => ({ default: m.SuperAdminPanel })));
const OmOssPage = lazy(() => import("./components/OmOssPage"));
const KundPage = lazy(() => import("./pages/KundPage"));
const JobbPage = lazy(() => import("./pages/JobbPage"));
const IntegritetspolicyPage = lazy(() => import("./components/IntegritetspolicyPage").then(m => ({ default: m.IntegritetspolicyPage })));
const PersonuppgiftsbitradesavtalPage = lazy(() => import("./components/PersonuppgiftsbitradesavtalPage").then(m => ({ default: m.PersonuppgiftsbitradesavtalPage })));
const AnvandarvillkorPage = lazy(() => import("./components/AnvandarvillkorPage").then(m => ({ default: m.AnvandarvillkorPage })));
const Terminal = lazy(() => import("./pages/Terminal"));
const ComingSoon = lazy(() => import("./components/ComingSoon"));
const LandingPage = lazy(() => import("./components/LandingPage"));
const RegisterPage = lazy(() => import("./components/RegisterPage"));
const LegalPage = lazy(() => import("./components/LegalPage"));
const FunktionerPage = lazy(() => import("./components/FunktionerPage"));
const FeatureRouter = lazy(() => import("./components/FeatureRouter"));
import { FEATURE_SEO, LIVE_SLUGS } from "./components/features/registry";
const PriserPage = lazy(() => import("./components/PriserPage"));
const JamforPage = lazy(() => import("./components/JamforPage"));
const KontaktPage = lazy(() => import("./components/KontaktPage"));
const HjalpPage = lazy(() => import("./components/HjalpPage"));
const BokaDemoPage = lazy(() => import("./components/BokaDemoPage"));
const OrdlistaPage = lazy(() => import("./components/OrdlistaPage"));
const StatusPage = lazy(() => import("./components/StatusPage"));
const BloggPage = lazy(() => import("./components/BloggPage"));
const OffertWidget = lazy(() => import("./pages/OffertWidget"));
const OffertStatusPage = lazy(() => import("./pages/OffertStatusPage"));
const PublicQuote = lazy(() => import("./pages/PublicQuote"));
const OffertDemoPage = lazy(() => import("./pages/OffertDemoPage"));
const PayInvoice = lazy(() => import("./pages/PayInvoice"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));
const DevelopersPage = lazy(() => import("./components/DevelopersPage"));
import { SupportChatWidget } from "./components/SupportChatWidget";
import VerificationGate from "./components/VerificationGate";
import { MaintenanceBanner } from "./components/MaintenanceBanner";

// ── Axios interceptors ──
axios.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (!config || config._retryCount >= 2) return Promise.reject(error);
  const status = error.response?.status;
  if (status === 520 || status === 502 || status === 503) {
    config._retryCount = (config._retryCount || 0) + 1;
    await new Promise(r => setTimeout(r, 1000 * config._retryCount));
    return axios(config);
  }
  return Promise.reject(error);
});

axios.interceptors.request.use((config) => {
  const tenantId = localStorage.getItem("tenantId");
  if (tenantId && config.url?.includes("/api/")) {
    config.headers["X-Tenant-ID"] = tenantId;
  }
  return config;
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Route detection helpers ──
function getRouteFlags(effectivePath, pathname, urlParams, detectedSlug, pathTenant) {
  return {
    isAdminRoute: effectivePath.startsWith('/admin') || urlParams.get('admin') === 'true' || (detectedSlug && effectivePath === '/'),
    isWorkerRoute: effectivePath.startsWith('/worker') || effectivePath.startsWith('/arbetare') || urlParams.get('worker') === 'true',
    isTerminalRoute: effectivePath === '/terminal' || effectivePath.startsWith('/terminal'),
    isSuperAdminRoute: pathname.startsWith('/superadmin') && !pathTenant,
    isLoginRoute: pathname === '/login',
    isAppEntryRoute: pathname === '/app',
    isSetPasswordRoute: pathname === '/aterstall-losenord',
    isRegisterRoute: pathname === '/register',
    isLegalRoute: pathname === '/legal',
    isJobbRoute: pathname.startsWith('/jobb/'),
    isKundRoute: pathname.startsWith('/kund/'),
    isFunktionerRoute: pathname === '/funktioner',
    isFeatureRoute: pathname.startsWith('/funktioner/'),
    isPriserRoute: pathname === '/priser',
    isJamforRoute: pathname === '/jamfor',
    isKontaktRoute: pathname === '/kontakt',
    isHjalpRoute: pathname === '/hjalp',
    isBokaDemoRoute: pathname === '/boka-demo',
    isOrdlistaRoute: pathname === '/ordlista',
    isStatusRoute: pathname === '/status',
    isBloggRoute: pathname === '/blogg',
    isOffertRoute: pathname.startsWith('/offert/'),
    isOffertSignRoute: pathname.startsWith('/offert-sign/'),
    isOffertStatusRoute: pathname.startsWith('/offert-status/'),
    isOffertDemoRoute: pathname.startsWith('/offert-demo/') || pathname === '/offert-demo',
    isPayRoute: pathname.startsWith('/pay/'),
    isDownloadRoute: pathname === '/download' || pathname.startsWith('/download/'),
    isDevelopersRoute: pathname === '/developers',
  };
}

function getPageFromPath(pathname, urlParams) {
  return PAGE_PATH_MAP[pathname] || urlParams.get('page') || null;
}

function isSaasRoute(isTenantRoute, pathname, appMode) {
  if (isTenantRoute) return false;
  if (pathname === '/saas') return true;
  if (appMode === 'saas' && pathname === '/') return true;
  const h = window.location.hostname;
  return pathname === '/' && h !== 'localhost' && PLATFORM_MAIN_HOSTS.includes(h);
}

// ── Main App ──
function App() {
  // Auth states
  // Restore sessions synchronously: the first render must already know whether a session exists,
  // otherwise <RedirectToLogin/> fires its redirect before the restore effect has run.
  const _readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  const _readJSON = (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } };
  const [adminUser, setAdminUser] = useState(() => (_readLS("adminToken") ? _readJSON("adminUser") : null));
  const [adminToken, setAdminToken] = useState(() => (_readJSON("adminUser") ? _readLS("adminToken") : null));
  const [workerUser, setWorkerUser] = useState(() => (_readLS("workerToken") ? _readJSON("workerUser") : null));
  const [workerToken, setWorkerToken] = useState(() => (_readJSON("workerUser") ? _readLS("workerToken") : null));
  const [superAdminUser, setSuperAdminUser] = useState(() => (_readLS("superadminToken") ? _readJSON("superadminUser") : null));
  const [superAdminToken, setSuperAdminToken] = useState(() => (_readJSON("superadminUser") ? _readLS("superadminToken") : null));

  // Booking state

  // Tenant resolution
  const [tenantData, setTenantData] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState(null);

  // ── Derived route context ──
  const pathTenant = detectTenantFromPath();
  const subdomainSlug = detectTenantSlugFromHost();
  const detectedSlug = pathTenant?.slug || subdomainSlug;
  const tenantSubpath = pathTenant?.subpath || window.location.pathname;

  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const appMode = process.env.REACT_APP_MODE || 'company';
  const effectivePath = pathTenant ? tenantSubpath : pathname;
  // Allow previewing the platform mask for testing via ?preview=platform
  const previewAs = urlParams.get("preview");
  const previewPlatform = previewAs === "platform";
  const isTenantRoute = !!detectedSlug;

  // ── Coming Soon override from Super Admin (live toggle, no rebuild) ──
  const [publicFlags, setPublicFlags] = useState(() => {
    try {
      const cached = sessionStorage.getItem("servora_public_flags");
      return cached ? JSON.parse(cached) : { coming_soon_platform: true };
    } catch { return { coming_soon_platform: true }; }
  });
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/superadmin/platform-settings/public`)
      .then(r => r.json())
      .then(data => {
        setPublicFlags(data);
        try { sessionStorage.setItem("servora_public_flags", JSON.stringify(data)); } catch {}
      })
      .catch(() => {});
  }, []);

  const routes = getRouteFlags(effectivePath, pathname, urlParams, detectedSlug, pathTenant);
  const currentPage = getPageFromPath(pathname, urlParams);


  // ── Error reporter ──
  useEffect(() => {
    import("./utils/errorReporter").then(({ setupErrorReporter }) => {
      let source = "unknown";
      if (pathname.includes("/admin")) source = "admin";
      else if (pathname.includes("/terminal")) source = "terminal";
      else if (pathname.includes("/arbetarportal") || pathname.includes("/worker")) source = "worker_portal";
      setupErrorReporter(source, detectedSlug || "");
    });
  }, [detectedSlug]);

  // ── SEO for public marketing pages ──
  useEffect(() => {
    const SEO = {
      '/funktioner': ["Funktioner — allt städföretaget behöver | Servora", "Schema, app för teamet, fakturering, RUT och mer — samlat i en plattform."],
      '/priser': ["Priser — enkla planer för städföretag | Servora", "Se Servoras planer och priser. 14 dagar gratis att prova."],
      '/jamfor': ["Jämför — varför städföretag väljer Servora | Servora", "En ärlig jämförelse mot kalkylark och generiska system."],
      '/hjalp': ["Hjälpcenter — vanliga frågor & felsökning | Servora", "Installera apparna, logga in, terminalen och arbetarportalen — svar på vanliga frågor och steg-för-steg-lösningar."],
      '/kontakt': ["Kontakt | Servora", "Kontakta Servora — vi svarar snabbt på frågor och demobokningar."],
      '/boka-demo': ["Boka demo | Servora", "Boka en personlig demo och se hur Servora förenklar din vardag."],
      '/ordlista': ["Ordlista — begrepp för städföretag | Servora", "RUT, ROT, AGI, Autogiro, Kivra och fler begrepp förklarade enkelt."],
      '/status': ["Driftstatus | Servora", "Aktuell driftstatus för Servoras tjänster."],
      '/om-oss': ["Om oss — byggt av branschen | Servora", "Servora är byggt av någon som själv jobbat som städare. Läs historien bakom plattformen för städföretag."],
      '/blogg': ["Blogg | Servora", "Tips och guider för städ- och serviceföretag."],
      ...FEATURE_SEO,
    };
    let entry = SEO[pathname];
    if (!entry && pathname === '/' && !detectedSlug) entry = ["Servora — plattform för städ- och serviceföretag", "Schema och kalender, app för teamet, fakturering och RUT — allt i en plattform. Prova gratis i 14 dagar."];
    if (!entry && pathname.startsWith('/funktioner/')) entry = ["Funktionen hittades inte | Servora", "Sidan du letar efter finns inte. Utforska alla funktioner i Servora."];
    if (!entry) return;
    const [title, desc] = entry;
    document.title = title;
    const setMeta = (selector, kind, key) => {
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('meta'); el.setAttribute(kind, key); document.head.appendChild(el); }
      return el;
    };
    setMeta('meta[name="description"]', 'name', 'description').setAttribute('content', desc);
    setMeta('meta[property="og:title"]', 'property', 'og:title').setAttribute('content', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description').setAttribute('content', desc);
    const featureSoon = pathname.startsWith('/funktioner/') && !LIVE_SLUGS.includes(pathname.split('/').filter(Boolean)[1]);
    setMeta('meta[name="robots"]', 'name', 'robots').setAttribute('content', featureSoon ? 'noindex,follow' : 'index,follow');
    if (pathname === '/' && !detectedSlug && !document.getElementById('servora-jsonld')) {
      const s = document.createElement('script');
      s.type = 'application/ld+json'; s.id = 'servora-jsonld';
      s.text = JSON.stringify({ "@context": "https://schema.org", "@graph": [
        { "@type": "Organization", "name": "Servora", "url": window.location.origin, "email": "hej@servora.se" },
        { "@type": "SoftwareApplication", "name": "Servora", "applicationCategory": "BusinessApplication", "operatingSystem": "Web, iOS, Android", "offers": { "@type": "Offer", "price": "239", "priceCurrency": "SEK", "priceSpecification": { "@type": "UnitPriceSpecification", "price": "239", "priceCurrency": "SEK", "valueAddedTaxIncluded": false } }, "description": desc },
      ] });
      document.head.appendChild(s);
    }
  }, [pathname, detectedSlug]);

  // ── Auth restoration ──
  useEffect(() => {
    if (routes.isAdminRoute) {
      const token = localStorage.getItem("adminToken");
      const user = localStorage.getItem("adminUser");
      if (token && user) { setAdminToken(token); setAdminUser(JSON.parse(user)); }
    }
    if (routes.isWorkerRoute) {
      const token = localStorage.getItem("workerToken");
      const user = localStorage.getItem("workerUser");
      if (token && user) { setWorkerToken(token); setWorkerUser(JSON.parse(user)); }
    }
    if (routes.isSuperAdminRoute) {
      const token = localStorage.getItem("superadminToken");
      const user = localStorage.getItem("superadminUser");
      if (token && user) { setSuperAdminToken(token); setSuperAdminUser(JSON.parse(user)); }
    }
  }, [routes.isAdminRoute, routes.isWorkerRoute, routes.isSuperAdminRoute]);

  // ── Tenant resolution ──
  useEffect(() => {
    if (!detectedSlug) {
      if (isCustomDomainHost()) {
        const hostname = window.location.hostname;
        const knownSlug = KNOWN_CUSTOM_DOMAINS[hostname];
        if (knownSlug) {
          localStorage.setItem("tenantSlug", knownSlug);
          localStorage.setItem("customDomainTenant", knownSlug);
        }
        axios.get(`${API}/resolve-domain?host=${hostname}`)
          .then(res => {
            if (res.data.resolved) {
              localStorage.setItem("tenantId", res.data.tenant_id);
              localStorage.setItem("tenantSlug", res.data.slug);
              localStorage.setItem("tenantName", res.data.name);
              localStorage.setItem("customDomainTenant", res.data.slug);
            }
          })
          .catch(() => {});
      }
      return;
    }
    const cached = localStorage.getItem("tenantSlug");
    if (cached === detectedSlug && localStorage.getItem("tenantId")) {
      setTenantData({ slug: detectedSlug, tenant_id: localStorage.getItem("tenantId"), name: localStorage.getItem("tenantName") || detectedSlug });
      return;
    }
    setTenantLoading(true);
    axios.get(`${API}/admin/ssl/resolve/${detectedSlug}`)
      .then(res => {
        localStorage.setItem("tenantId", res.data.tenant_id);
        localStorage.setItem("tenantSlug", res.data.slug);
        localStorage.setItem("tenantName", res.data.name);
        setTenantData(res.data);
        setTenantError(null);
      })
      .catch(() => {
        setTenantError("Företaget hittades inte");
        localStorage.removeItem("tenantId");
        localStorage.removeItem("tenantSlug");
        localStorage.removeItem("tenantName");
      })
      .finally(() => setTenantLoading(false));
  }, [detectedSlug]);

  // ── Auth handlers ──
  const handleAdminLogout = () => { setAdminUser(null); setAdminToken(null); localStorage.removeItem("adminToken"); localStorage.removeItem("adminUser"); localStorage.removeItem("tenantId"); localStorage.removeItem("servoraUnified"); };
  const handleWorkerLogout = () => { setWorkerUser(null); setWorkerToken(null); localStorage.removeItem("workerToken"); localStorage.removeItem("workerUser"); localStorage.removeItem("servoraUnified"); };
  const handleSuperAdminLogin = (user, token) => { setSuperAdminUser(user); setSuperAdminToken(token); };
  const handleSuperAdminLogout = () => { setSuperAdminUser(null); setSuperAdminToken(null); localStorage.removeItem("superadminToken"); localStorage.removeItem("superadminUser"); };

  // ── Early returns: loading / error states ──
  if (detectedSlug && tenantLoading) return <TenantLoading slug={detectedSlug} />;
  if (detectedSlug && tenantError) return <TenantError slug={detectedSlug} />;

  // ── Standalone routes (no shell) ──
  if (routes.isTerminalRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><Terminal /><Toaster position="top-center" theme="dark" /></div></Suspense>;
  if (routes.isAppEntryRoute) return <AppEntry />;
  if (routes.isLoginRoute) return <Suspense fallback={<LaunchFallback />}><div className="App" data-testid="app-container"><UnifiedLogin /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isSetPasswordRoute) return <Suspense fallback={<LaunchFallback />}><div className="App" data-testid="app-container"><SetPasswordPage /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isRegisterRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><RegisterPage /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isKundRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><KundPage /></div></Suspense>;
  if (routes.isJobbRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><JobbPage /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isLegalRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><LegalPage /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isFeatureRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><FeatureRouter /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isFunktionerRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><FunktionerPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isPriserRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><PriserPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isJamforRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><JamforPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isHjalpRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><HjalpPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isKontaktRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><KontaktPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isBokaDemoRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><BokaDemoPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isOrdlistaRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><OrdlistaPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isStatusRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><StatusPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isBloggRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><BloggPage /><CookieBanner /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isOffertSignRoute) return <Suspense fallback={<LazyFallback />}><PublicQuote /><Toaster position="top-center" /></Suspense>;
  if (routes.isOffertRoute) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="offert-widget-disabled">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Sidan är inte tillgänglig</h1>
        <p className="text-sm text-slate-500">Kontakta företaget direkt för en offertförfrågan.</p>
      </div>
    </div>
  );
  if (routes.isOffertStatusRoute) return <Suspense fallback={<LazyFallback />}><OffertStatusPage /><Toaster position="top-center" /></Suspense>;
  if (routes.isOffertDemoRoute) return <Suspense fallback={<LazyFallback />}><OffertDemoPage /><Toaster position="top-center" /></Suspense>;
  if (routes.isPayRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><PayInvoice /><Toaster position="top-center" /></div></Suspense>;
  if (routes.isDownloadRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><DownloadPage /><Toaster position="top-right" /></div></Suspense>;
  if (routes.isDevelopersRoute) return <Suspense fallback={<LazyFallback />}><div className="App" data-testid="app-container"><DevelopersPage /><Toaster position="top-right" /></div></Suspense>;

  // ── Landing pages ──
  // Coming Soon mask applies ONLY on production hosts (www.servora.se).
  // Preview / dev environments always show the real LandingPage so the team can test freely.
  const isProductionHost = typeof window !== 'undefined' && isProductionHostName(window.location.hostname);
  // ?preview=platform lets the team see the real site on the production host while the mask is on.
  const comingSoonPlatformActive = isProductionHost && publicFlags.coming_soon_platform !== false && !previewPlatform;

  // Preview override (takes priority over regular hostname-based routing for testing)
  // www.servora.se (or ?preview=platform): show ComingSoon only on prod host with flag ON
  if (isSaasRoute(isTenantRoute, pathname, appMode) || previewPlatform) {
    if (comingSoonPlatformActive) {
      // No SupportChatWidget while masked — customers shouldn't reach support before launch
      return <div className="App" data-testid="app-container"><Suspense fallback={<LazyFallback />}><ComingSoon /></Suspense><CookieBanner /><Toaster position="top-right" /></div>;
    }
    return <div className="App" data-testid="app-container"><Suspense fallback={<LazyFallback />}><LandingPage /></Suspense><SupportChatWidget /><CookieBanner /><Toaster position="top-right" /></div>;
  }

  // ── Authenticated app routes ──
  if (routes.isSuperAdminRoute) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <div className="App" data-testid="app-container">
          {!superAdminUser || !superAdminToken
            ? <SuperAdminLogin onLoginSuccess={handleSuperAdminLogin} />
            : <SuperAdminPanel user={superAdminUser} token={superAdminToken} onLogout={handleSuperAdminLogout} />}
          <Toaster position="top-right" />
        </div>
      </Suspense>
    );
  }

  if (routes.isAdminRoute) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <div className="App" data-testid="app-container">
          <MaintenanceBanner />
          {!adminUser || !adminToken
            ? <RedirectToLogin />
            : <VerificationGate token={adminToken} onLogout={handleAdminLogout}><AdminPanel user={adminUser} token={adminToken} onLogout={handleAdminLogout} /></VerificationGate>}
          {adminUser && adminToken && <SupportChatWidget token={adminToken} />}
          <Toaster position="top-right" />
        </div>
      </Suspense>
    );
  }


  if (routes.isWorkerRoute) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <div className="App" data-testid="app-container">
          <MaintenanceBanner />
          {!workerUser || !workerToken
            ? <RedirectToLogin />
            : <WorkerPortal user={workerUser} token={workerToken} onLogout={handleWorkerLogout} />}
          <Toaster position="top-right" />
        </div>
      </Suspense>
    );
  }

  // ── Static public pages ──
  const staticPages = {
    'om-oss': OmOssPage,
    'integritetspolicy': IntegritetspolicyPage,
    'anvandarvillkor': AnvandarvillkorPage,
    'personuppgiftsbitradesavtal': PersonuppgiftsbitradesavtalPage,
  };

  if (currentPage && staticPages[currentPage]) {
    const PageComponent = staticPages[currentPage];
    return <Suspense fallback={<LazyFallback />}><PageComponent /><CookieBanner /><Toaster position="top-right" /></Suspense>;
  }

  // Anything else: real 404 (no demo/tenant fallback site is ever shown)
  return (
    <div className="App" data-testid="app-container">
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="platform-404">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Sidan hittades inte</h1>
          <p className="text-sm text-slate-500 mb-4">Adressen finns inte på Servora.</p>
          <a href="/" className="text-sm font-semibold text-emerald-700 underline">Till startsidan</a>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
